import { useCallback, useRef } from 'react';
import { ListOnScrollProps } from 'react-window';
import { Connection } from './types/paginationTypes';

export type OnScrollArgs = ListOnScrollProps;

export interface OnScrollData {
  endCursor?: string | null;
  hasNextPage?: boolean;
  templates?: unknown[];
}

export interface UseOnScrollProps<T extends Connection | OnScrollData> {
  data: T;
  fetchMoreCallback: (cursor: string) => Promise<unknown>;
  onError?: (error: Error) => void;
  needMoreRecords?: (offSetHeight: number) => boolean;
}

/**
 * custom function that allows to indetify how many rows are left to scroll (depending on the table size) and
   how long the user has scrolled.
 * @param offSetHeight: number -> is the amount of pixels the user has scrolled.
 * @param tableBodyHeight: number -> the height in pixels of the table body.
 * @param cachedRows: number -> total of records already requested (by standard every request gets 25 records).
 * @param rowHeightSize: number -> height of each row displayed in the grid (by default is 55 pixels).
 * @param fetchEarly: boolean -> if true, the fetch call will be executed before reaching the last previous fetched record.
*/
const dynamicGetMoreRecords = (
  offSetHeight: number,
  tableBodyHeight = 0,
  cachedRows: number,
  fetchEarly = false,
  rowHeightSize = 55,
) => {
  const rowsPerPage = tableBodyHeight / rowHeightSize;
  const rowsToScroll = !fetchEarly ? cachedRows - rowsPerPage : (cachedRows - rowsPerPage) / 1.5;
  return rowsToScroll < offSetHeight / rowHeightSize;
};

export const useDynamicGetMoreRecords = (
  tableBodyHeight = 0,
  cachedRows: number,
  fetchEarly = false,
  rowHeightSize = 55,
) => {
  return useCallback(
    (offSetHeight: number) => {
      return dynamicGetMoreRecords(offSetHeight, tableBodyHeight, cachedRows, fetchEarly, rowHeightSize);
    },
    [tableBodyHeight, cachedRows, fetchEarly, rowHeightSize],
  );
};

export const useOnScroll = <T extends Connection | OnScrollData>({
  needMoreRecords = (offSetHeight: number) => Boolean(offSetHeight > 0),
  ...props
}: UseOnScrollProps<T>): ((args: OnScrollArgs) => void) => {
  const fetching = useRef<boolean | null>(null);

  async function onScroll(args: OnScrollArgs) {
    const hasMoreRecords =
      'hasNextPage' in props.data ? props.data.hasNextPage : (props.data as Connection)?.pageInfo?.hasNextPage ?? true;
    // scrollDirection is either "forward" or "backward". -> if backwards dont ask for more
    // scrollOffset is a number. -> bottom of viewport -> ~ each record is ~ 55 offset
    // FixedSizeList calls this function at start with { scrollDirection: 'forward', offSetHeight: 0 }
    if (
      args.scrollDirection === 'forward' &&
      hasMoreRecords &&
      needMoreRecords(args.scrollOffset) &&
      !fetching.current
    ) {
      fetching.current = true;
      try {
        const endCursor =
          'endCursor' in props.data ? props.data.endCursor : (props.data as Connection)?.pageInfo?.endCursor;
        await props.fetchMoreCallback(endCursor ?? '');
      } catch (error) {
        props?.onError?.(error);
      } finally {
        fetching.current = false;
      }
    }
  }
  return onScroll;
};
